import React, { useRef, useState } from 'react';
import {
  DAMAGE_VIEWBOX,
  MARKER_COLOR,
  nearestZoneLabel,
  normalizeDamages,
  makeDamagePoint,
  damageDiagramInner,
} from '../utils/carDamage';

/**
 * Interactive vehicle-damage marker tool (insurance-report style).
 *
 * - The car illustration stays CLEAN (no auto-drawn dents/scratches).
 * - Tap anywhere on the diagram to drop a red "X" at the exact damage spot.
 * - Drag an X to move it. Tap to select. Delete or add a note from the list.
 * - Works on Desktop, Android and iPhone (pointer events + SVG CTM mapping,
 *   so the coordinate is correct regardless of zoom / scroll / DPR).
 *
 * Controlled component: value = array of points { id, x, y, note }.
 */
export default function CarDamageSelector({ value, onChange, isAr = false }) {
  const points = normalizeDamages(value);
  const svgRef = useRef(null);
  const dragRef = useRef({ id: null, moved: false });
  const [selectedId, setSelectedId] = useState(null);

  const emit = (next) => onChange && onChange(next);

  // Convert a pointer event to viewBox coordinates (device-accurate).
  const toSvgPoint = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  // Click on empty area -> add a new X there.
  const handleBackgroundPointerDown = (evt) => {
    // ignore if a marker handled it
    if (dragRef.current.id) return;
    const p = toSvgPoint(evt);
    if (!p) return;
    const np = makeDamagePoint(p.x, p.y);
    emit([...points, np]);
    setSelectedId(np.id);
  };

  const handleMarkerPointerDown = (evt, id) => {
    evt.stopPropagation();
    setSelectedId(id);
    dragRef.current = { id, moved: false };
    if (evt.currentTarget.setPointerCapture && evt.pointerId != null) {
      try { evt.currentTarget.setPointerCapture(evt.pointerId); } catch (_) {}
    }
  };

  const handlePointerMove = (evt) => {
    if (!dragRef.current.id) return;
    const p = toSvgPoint(evt);
    if (!p) return;
    dragRef.current.moved = true;
    emit(points.map((pt) => (pt.id === dragRef.current.id ? { ...pt, x: p.x, y: p.y } : pt)));
  };

  const endDrag = () => {
    dragRef.current = { id: null, moved: false };
  };

  const removePoint = (id) => {
    emit(points.filter((p) => p.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const setNote = (id, note) =>
    emit(points.map((p) => (p.id === id ? { ...p, note } : p)));

  const clearAll = () => {
    emit([]);
    setSelectedId(null);
  };

  return (
    <div className="mt-4 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
          <span>🚗</span>
          {isAr ? 'تحديد مواقع الأضرار' : 'Localisation des dommages'}
        </h3>
        {points.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-bold text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition"
          >
            ✕ {isAr ? 'مسح الكل' : 'Tout effacer'}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        {isAr
          ? 'انقر على رسم السيارة لوضع علامة "X" في مكان الضرر. اسحب لتحريكها، وانقر للتحديد أو الحذف.'
          : 'Cliquez sur le schéma pour placer un « X » à l’endroit du dommage. Glissez pour déplacer, sélectionnez pour supprimer.'}
      </p>

      <div className="flex flex-col md:flex-row gap-4">
        {/* Diagram — top-down single view */}
        <div className="flex justify-center md:justify-start">
          <div className="select-none" style={{ width: '160px', flexShrink: 0 }}>
            <svg
              ref={svgRef}
              viewBox={DAMAGE_VIEWBOX}
              preserveAspectRatio="xMidYMid meet"
              className="w-full h-auto rounded-xl bg-slate-50 border border-slate-200 cursor-crosshair shadow-sm"
              style={{ touchAction: 'none', display: 'block' }}
              onPointerDown={handleBackgroundPointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerLeave={endDrag}
            >
              {/* Top-down car schematic (decorative, not clickable) */}
              <g style={{ pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: damageDiagramInner(isAr) }} />

              {/* X markers */}
              {points.map((p, i) => {
                const sel = p.id === selectedId;
                const s = 8;
                return (
                  <g
                    key={p.id}
                    onPointerDown={(e) => handleMarkerPointerDown(e, p.id)}
                    style={{ cursor: 'grab' }}
                  >
                    {sel && (
                      <circle cx={p.x} cy={p.y} r={s + 8} fill={MARKER_COLOR} fillOpacity="0.15" stroke={MARKER_COLOR} strokeWidth="1" strokeDasharray="3 2" />
                    )}
                    <circle cx={p.x} cy={p.y} r={s + 4} fill="#ffffff" fillOpacity="0.95" stroke={MARKER_COLOR} strokeWidth="1.4" />
                    <line x1={p.x - s} y1={p.y - s} x2={p.x + s} y2={p.y + s} stroke={MARKER_COLOR} strokeWidth="2.4" strokeLinecap="round" />
                    <line x1={p.x - s} y1={p.y + s} x2={p.x + s} y2={p.y - s} stroke={MARKER_COLOR} strokeWidth="2.4" strokeLinecap="round" />
                    <circle cx={p.x + s + 4} cy={p.y - s - 4} r="6" fill="#111827" stroke="#fff" strokeWidth="1" />
                    <text x={p.x + s + 4} y={p.y - s - 1.5} textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none' }}>
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Marker list */}
        <div className="flex-1">
          {points.length === 0 ? (
            <div className="text-center py-8 px-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg text-xs text-slate-400">
              {isAr
                ? 'لا توجد علامات بعد. انقر على رسم السيارة لإضافة علامة "X".'
                : 'Aucune marque. Cliquez sur le schéma pour ajouter un « X ».'}
            </div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {points.map((p, i) => {
                const sel = p.id === selectedId;
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    className={`rounded-lg p-2.5 border-2 transition cursor-pointer ${
                      sel ? 'border-red-300 bg-red-50/60' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="flex-1 text-xs font-bold text-slate-800 truncate">
                        {nearestZoneLabel(p.x, p.y, isAr)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removePoint(p.id); }}
                        className="flex-shrink-0 text-red-500 hover:text-white hover:bg-red-500 text-xs font-bold w-6 h-6 rounded-md border border-red-200 transition"
                        title={isAr ? 'حذف' : 'Supprimer'}
                      >
                        ✕
                      </button>
                    </div>
                    <input
                      type="text"
                      value={p.note || ''}
                      onChange={(e) => setNote(p.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder={isAr ? 'وصف الضرر (اختياري)…' : 'Description du dommage (optionnel)…'}
                      className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-red-200 bg-slate-50"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {points.length > 0 && (
            <div className="mt-2 text-[11px] font-semibold text-slate-500 text-center">
              {points.length}{' '}
              {isAr ? 'علامة مسجّلة' : points.length > 1 ? 'marques placées' : 'marque placée'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
